// 3rd party libs
import React, { ReactElement } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { useTranslation } from 'react-i18next';
import { Map, MapMarker } from 'components';
import {
  MachineMapContainer,
  MapInformationContainer,
  SiteViewContainer
} from './SiteMapIndex.elements';
import { WidgetUi } from 'common/ui';
import { useGetPlantByIdQueryInfo } from '../hooks/useGetPlantByIdQueryInfo';

interface MapCardProps {
  plantId: string;
  zoom?: number;
  heading?: string;
  markerIcon?: string | google.maps.Icon | google.maps.Symbol;
  height?: number;
  mapTypeControl?: boolean;
}

const headerRenderer = (heading?: string, address?: string) => {
  return (
    <MapInformationContainer>
      <div className="site-map-title">{heading}</div>
      <div className="site-map-information">{address}</div>
    </MapInformationContainer>
  );
};

const render = (status: Status) => {
  return <h1>{status}</h1>;
};

const MapCard = ({
  plantId,
  zoom,
  heading,
  markerIcon,
  height,
  mapTypeControl
}: MapCardProps): ReactElement => {
  
  const { data: plant, isFetching, error } = useGetPlantByIdQueryInfo({ plantId });

  const { t } = useTranslation(['mh']);
  heading = t('site_location') as string;
  const center = {
    lat: plant?.latitude as number,
    lng: plant?.longitude as number
  };

  const mapAddress = plant
    ? [
        plant.addressLine1,
        plant.addressLine2,
        plant.city,
        plant.state,
        plant.countryName,
        plant.zipCode
      ]
        .filter((part) => !!part?.trim())
        .join(', ')
    : '';

  return (
    <SiteViewContainer>
      <WidgetUi
        className="site-map-widget"
        isLoading={isFetching}
        error={error && (t('failed_to_load_data', { ns: 'common' }) as string)}
      >
        <MachineMapContainer height={height}>
          <Wrapper apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string} render={render}>
            <Map
              mapTypeControl={mapTypeControl ? mapTypeControl : false}
              center={center}
              zoom={zoom}
              style={{ flexGrow: '1', height: '100%' }}
            >
              <MapMarker icon={markerIcon} key={`key-${mapAddress}`} position={center} />
            </Map>
          </Wrapper>
        </MachineMapContainer>
        {headerRenderer(heading, mapAddress)}
      </WidgetUi>
    </SiteViewContainer>
  );
};

MapCard.defaultProps = {
  zoom: 14,
  markerIcon: {
    url: '/src/icons/img/marker.png'
  }
};

export default MapCard;
